import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import analytics from "../lib/analytics";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Star, AlertTriangle, Flame, Activity, Heart, FlaskConical,
  Pill as PillIcon, TrendingUp, ClipboardCheck, AlertCircle, LogOut,
  MapPin, Lightbulb, BookOpen, Users, ArrowUpRight, Shield, MessageSquare, Hash
} from "lucide-react";
import GuidelineReviewNotice from "../components/GuidelineReviewNotice.jsx";
import ReviewStatusBadge from "../components/ReviewStatusBadge.jsx";

function Section({ icon: Icon, title, color = "text-[#111827]", children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-slate-200 rounded-lg bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50"
        data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${color}`} />
          <h3 className="font-heading font-semibold text-[#111827]">{title}</h3>
        </div>
        <span className="text-slate-400 text-sm">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-5 pb-5 pt-1">{children}</div>}
    </section>
  );
}

function BulletList({ items }) {
  if (!items || items.length === 0) return <p className="text-sm text-slate-500 italic">Not specified.</p>;
  return (
    <ul className="space-y-1.5 text-sm text-slate-700">
      {items.map((x, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-[#111827] mt-1">•</span>
          <span>{typeof x === "string" ? x : JSON.stringify(x)}</span>
        </li>
      ))}
    </ul>
  );
}

const severityBadge = (s) => {
  const map = {
    critical: "bg-rose-100 text-rose-800 border-rose-200",
    severe: "bg-rose-100 text-rose-800 border-rose-200",
    high: "bg-amber-100 text-amber-800 border-amber-200",
    moderate: "bg-amber-100 text-amber-800 border-amber-200",
    mild: "bg-slate-100 text-slate-700 border-slate-200",
    low: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return map[s?.toLowerCase()] || "bg-slate-100 text-slate-700 border-slate-200";
};

// Generic/templated filler that was auto-generated across many protocols. We
// suppress it at render so users never see vague boilerplate (condition-specific
// content is added via the clinician review workflow).
const norm = (s) => (typeof s === "string" ? s : JSON.stringify(s)).toLowerCase().replace(/\s+/g, " ").trim();
const FILLER_PHRASES = [
  "document time of onset, interventions, and response meticulously",
  "early senior involvement improves outcomes in critical presentations",
  "always consider hiv status in the south african clinical context",
  "see specific protocol guidelines for risk factors",
  "refer to local hospital guidelines",
  "see local hospital statistics for regional prevalence",
];
const isFillerText = (t) => {
  const n = norm(t || "");
  if (!n) return true;
  if (n.includes("refer to ndoh stg/eml 2024 and south african disease burden")) return true;
  if (n.includes("see local hospital statistics for regional prevalence")) return true;
  return FILLER_PHRASES.some((f) => n === f || n.includes(f));
};
const stripFiller = (items) => (Array.isArray(items) ? items.filter((x) => !isFillerText(x)) : []);
// Two arrays are duplicates if their normalised item sets are identical (ignores case).
const sameItems = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a.map(norm)].sort();
  const sb = [...b.map(norm)].sort();
  return sa.every((v, i) => v === sb[i]);
};

export default function ProtocolDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [fav, setFav] = useState(false);
  const viewTrackedId = useRef(null);

  useEffect(() => {
    api.get(`/protocols/${id}`).then((r) => {
      setP(r.data);
      // Guard against StrictMode double-fetch in dev — one protocol_view per id.
      if (viewTrackedId.current !== r.data.protocol_id) {
        viewTrackedId.current = r.data.protocol_id;
        analytics.protocolView(r.data.title, r.data.category);
      }
      api.post("/recent", { item_type: "protocol", item_id: r.data.protocol_id, title: r.data.title }).catch(() => {});
    });
    api.get("/favorites").then((r) => setFav(r.data.some((f) => f.item_type === "protocol" && f.item_id === id))).catch(() => {});
  }, [id]);

  // Engagement: protocol_read_time (on leave) + scroll depth milestones.
  useEffect(() => {
    if (!p) return;
    const name = p.title;
    const start = Date.now();
    const firedDepths = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      [25, 50, 75, 100].forEach((t) => {
        if (pct >= t && !firedDepths.has(t)) {
          firedDepths.add(t);
          analytics.scrollDepth(name, t);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      const secs = Math.round((Date.now() - start) / 1000);
      if (secs >= 3) analytics.protocolReadTime(name, secs);
    };
  }, [p?.protocol_id]);

  const toggleFav = async () => {
    if (!p) return;
    if (fav) {
      await api.delete(`/favorites/protocol/${id}`);
      setFav(false);
      toast.success("Removed from favorites");
    } else {
      await api.post("/favorites", { item_type: "protocol", item_id: id, title: p.title });
      setFav(true);
      toast.success("Added to favorites");
    }
  };

  if (!p) return <div className="text-slate-500 text-sm">Loading protocol...</div>;

  const hasArr = (arr) => Array.isArray(arr) && arr.length > 0;
  const steps = p.algorithm_steps || [];
  const dosing = p.drug_dosing || {};
  // Bug fixes: suppress generic filler + hide clinical-features when it just
  // duplicates presenting-complaints (a known content-generation artifact).
  const riskFactors = stripFiller(p.risk_factors);
  const pearls = stripFiller(p.clinical_pearls);
  const showEpidemiology = p.epidemiology && !isFillerText(p.epidemiology);
  const showClinicalFeatures = hasArr(p.clinical_features) && !sameItems(p.clinical_features, p.presenting_complaints);

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="protocol-detail">
      <Link to="/app/protocols" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to protocols
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {p.is_emergency && (
                <Badge className="bg-rose-600 text-white border-0">
                  <Flame className="w-3 h-3 mr-1" /> EMERGENCY
                </Badge>
              )}
              {p.severity && <Badge className={`border ${severityBadge(p.severity)} capitalize`}>{p.severity}</Badge>}
              <Badge className="bg-slate-100 text-slate-700 border-0 capitalize">{(p.category || "").replace(/_/g, " ")}</Badge>
              {p.version && <Badge className="bg-slate-50 text-slate-500 border border-slate-200">v{p.version}</Badge>}
              <ReviewStatusBadge status={p.review_status} reviewedAt={p.reviewed_at} />
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#111827]">{p.title}</h1>
            {p.definition && <p className="text-slate-600 mt-3 leading-relaxed">{p.definition}</p>}
            {p.guideline_source && (
              <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                <Shield className="w-3 h-3" /> {p.guideline_source}
              </div>
            )}
          </div>
          <Button
            onClick={toggleFav}
            variant="outline"
            className={fav ? "border-amber-300 text-amber-700 bg-amber-50" : ""}
            data-testid="favorite-toggle"
          >
            <Star className={`w-4 h-4 mr-2 ${fav ? "fill-amber-400" : ""}`} />
            {fav ? "Favorited" : "Add favorite"}
          </Button>
        </div>
      </div>

      {/* Red flags FIRST - critical safety */}
      {hasArr(p.red_flags) && (
        <div className="relative bg-red-50/70 border border-red-200/70 rounded-2xl p-5 pl-6 shadow-soft overflow-hidden" data-testid="red-flags-callout">
          <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-red-600" />
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center">
              <AlertTriangle className="w-4.5 h-4.5 text-red-700" strokeWidth={2} />
            </div>
            <h3 className="font-heading font-bold text-red-900 text-lg">Red flags</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-red-900/90">
            {p.red_flags.map((rf, i) => (
              <li key={i} className="flex gap-2"><span className="text-red-600 mt-0.5">▸</span><span>{rf}</span></li>
            ))}
          </ul>
        </div>
      )}

      {/* Grid of sections */}
      <div className="grid lg:grid-cols-2 gap-4">
        {showEpidemiology && (
          <Section icon={Users} title="Epidemiology">
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{p.epidemiology}</p>
          </Section>
        )}
        {riskFactors.length > 0 && (
          <Section icon={AlertCircle} title="Risk factors">
            <BulletList items={riskFactors} />
          </Section>
        )}
        {hasArr(p.presenting_complaints) && (
          <Section icon={ClipboardCheck} title="Presenting complaints">
            <BulletList items={p.presenting_complaints} />
          </Section>
        )}
        {showClinicalFeatures && (
          <Section icon={Heart} title="Clinical features">
            <BulletList items={p.clinical_features} />
          </Section>
        )}
        {hasArr(p.diagnostic_criteria) && (
          <Section icon={ClipboardCheck} title="Diagnostic criteria">
            <BulletList items={p.diagnostic_criteria} />
          </Section>
        )}
        {hasArr(p.investigations) && (
          <Section icon={FlaskConical} title="Investigations">
            <BulletList items={p.investigations} />
          </Section>
        )}
        {hasArr(p.severity_classification) && (
          <Section icon={TrendingUp} title="Severity classification">
            <div className="space-y-2">
              {p.severity_classification.map((g, i) => (
                <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50">
                  <div className="font-semibold text-sm text-[#111827]">{g.grade}</div>
                  {g.criteria && <div className="text-xs text-slate-600 mt-1"><span className="font-medium">Criteria:</span> {g.criteria}</div>}
                  {g.management && <div className="text-xs text-slate-600"><span className="font-medium">Management:</span> {g.management}</div>}
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      {/* Initial stabilisation */}
      {hasArr(p.initial_stabilisation) && (
        <Section icon={Activity} title="Initial stabilisation" color="text-rose-700">
          <BulletList items={p.initial_stabilisation} />
        </Section>
      )}

      {/* Initial management */}
      {hasArr(p.initial_management) && (
        <Section icon={Activity} title="Initial management">
          <BulletList items={p.initial_management} />
        </Section>
      )}

      {/* Algorithm steps */}
      {hasArr(steps) && (
        <Section icon={ArrowUpRight} title="Algorithm / step-by-step">
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <div className="w-8 h-8 rounded-md bg-[#0B5FFF] text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
                  {s.step || i + 1}
                </div>
                <div className="flex-1">
                  {s.title && <div className="font-heading font-semibold text-[#111827] text-sm">{s.title}</div>}
                  {s.description && <p className="text-sm text-slate-700 mt-1">{s.description}</p>}
                  {typeof s === "string" && <p className="text-sm text-slate-700">{s}</p>}
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Drug dosing */}
      {(dosing.adult || dosing.paediatric) && (
        <Section icon={PillIcon} title="Drug dosing">
          {dosing.adult && (
            <div className="mb-4">
              <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold mb-2">Adult</div>
              <div className="space-y-2">
                {dosing.adult.map((d, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-md text-sm">
                    <div className="font-semibold text-[#111827]">{d.drug}</div>
                    <div className="text-slate-700">{d.dose}</div>
                    {d.frequency && <div className="text-xs text-slate-500">{d.frequency}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {dosing.paediatric && (
            <div>
              <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold mb-2">Paediatric</div>
              <div className="space-y-2">
                {dosing.paediatric.map((d, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md text-sm">
                    <div className="font-semibold text-[#111827]">{d.drug}</div>
                    <div className="text-slate-700">{d.dose}</div>
                    {d.frequency && <div className="text-xs text-slate-500">{d.frequency}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Treatment, monitoring, complications */}
      <div className="grid lg:grid-cols-2 gap-4">
        {hasArr(p.treatment_steps) && (
          <Section icon={Activity} title="Treatment steps">
            <BulletList items={p.treatment_steps} />
          </Section>
        )}
        {hasArr(p.monitoring) && (
          <Section icon={TrendingUp} title="Monitoring">
            <BulletList items={p.monitoring} />
          </Section>
        )}
        {hasArr(p.complications) && (
          <Section icon={AlertCircle} title="Complications">
            <BulletList items={p.complications} />
          </Section>
        )}
        {hasArr(p.escalation_criteria) && (
          <Section icon={ArrowUpRight} title="Escalation criteria">
            <BulletList items={p.escalation_criteria} />
          </Section>
        )}
        {hasArr(p.admission_criteria) && (
          <Section icon={ClipboardCheck} title="Admission criteria">
            <BulletList items={p.admission_criteria} />
          </Section>
        )}
        {hasArr(p.discharge_criteria) && (
          <Section icon={LogOut} title="Discharge criteria">
            <BulletList items={p.discharge_criteria} />
          </Section>
        )}
        {hasArr(p.follow_up_plan) && (
          <Section icon={ClipboardCheck} title="Follow-up plan">
            <BulletList items={p.follow_up_plan} />
          </Section>
        )}
        {hasArr(p.referral_criteria) && (
          <Section icon={ArrowUpRight} title="Referral criteria">
            <BulletList items={p.referral_criteria} />
          </Section>
        )}
      </div>

      {/* Patient counselling */}
      {hasArr(p.patient_counselling) && (
        <Section icon={MessageSquare} title="Patient counselling" color="text-emerald-700">
          <BulletList items={p.patient_counselling} />
        </Section>
      )}

      {/* ICD-10 codes */}
      {hasArr(p.icd10) && (
        <Section icon={Hash} title="ICD-10 codes" defaultOpen={false}>
          <div className="space-y-1.5">
            {p.icd10.map((c, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="font-mono font-semibold text-[#111827] min-w-[64px]">{c.code}</span>
                <span className="text-slate-700">{c.description}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* SA-specific notes */}
      {hasArr(p.sa_resource_notes) && (
        <Section icon={MapPin} title="SA resource notes" color="text-emerald-700" defaultOpen={false}>
          <BulletList items={p.sa_resource_notes} />
        </Section>
      )}

      {/* Pearls */}
      {pearls.length > 0 && (
        <Section icon={Lightbulb} title="Clinical pearls" color="text-amber-600" defaultOpen={false}>
          <BulletList items={pearls} />
        </Section>
      )}

      {/* References */}
      {hasArr(p.references) && (
        <Section icon={BookOpen} title="References" defaultOpen={false}>
          <ul className="space-y-1 text-sm text-slate-700">
            {p.references.map((r, i) => <li key={i} className="flex gap-2"><span className="text-slate-400">{i + 1}.</span><span>{typeof r === "string" ? r : (r.title || JSON.stringify(r))}</span></li>)}
          </ul>
        </Section>
      )}

      {/* Source attribution */}
      {p.source_attribution && typeof p.source_attribution === "object" && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-700 mb-1">Source attribution</div>
          {Object.entries(p.source_attribution).map(([k, v]) => (
            <div key={k}><span className="font-medium capitalize">{k.replace(/_/g, " ")}:</span> {typeof v === "string" ? v : JSON.stringify(v)}</div>
          ))}
        </div>
      )}

      <GuidelineReviewNotice source={p.guideline_source} />
    </div>
  );
}
